import { Injectable } from '@angular/core';
import { ToastType, YMRoutingService, YMToastService } from '@yupma/ui-kit';
import { Subject } from 'rxjs';
import { ActivityDocument, ActivityType, ContactDocument, EntityType } from 'server-model';
import { RouteLinkGenerator, RouteName } from 'src/app/routing-path';

export class AddActivityModalOptions {
  constructor(
    public state: boolean = false,
    public activityType: ActivityType | string = '',
    public entityType: EntityType | null = null,
    public entityId: string = '',
    public isLog: boolean = false,

  ) { }
}

@Injectable({
  providedIn: 'root'
})
export class UIService {

  private onTopbarTitleChanged = new Subject<string>();
  onTopbarTitleChangedEvent$ = this.onTopbarTitleChanged.asObservable();

  private onAddContactDrawerStateChanged = new Subject<boolean>();
  onAddContactDrawerStateChangedEvent$ = this.onAddContactDrawerStateChanged.asObservable();

  private onAddActivityModalStateChanged = new Subject<AddActivityModalOptions>();
  onAddActivityModalStateChangedEvent$ = this.onAddActivityModalStateChanged.asObservable();

  private onOrganisationChanged = new Subject();
  onOrganisationChangedEvent$ = this.onOrganisationChanged.asObservable();

  private onContactAdded = new Subject<ContactDocument>();
  onContactAddedEvent$ = this.onContactAdded.asObservable();

  private onActivityAdded = new Subject<ActivityDocument>();
  onActivityAddedEvent$ = this.onActivityAdded.asObservable();

  constructor(
    private routingService: YMRoutingService,
    private ymToastService: YMToastService,
  ) { }

  setTopbarTitle(title:string){
    this.onTopbarTitleChanged.next(title)
  }

  private convertArrayOfObjectsToCSV(data: any[]) {
    let csvContent = '';
    const headers = ['#', ...Object.keys(data[0])]; // Add 'count' as the first header
    csvContent += headers.join(',') + '\n';

    data.forEach((row, index) => {
      let rowData = [index + 1, ...headers.slice(1).map(header => {
        let value = JSON.stringify(row[header]); // Stringify all values
        // Escape quotes by doubling them and wrap the entire value in quotes
        if (typeof value === 'string') {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })];
      csvContent += rowData.join(',') + '\n';
    });

    return csvContent;
  }

  downloadCSV(data: any[], fileName: string = 'data') {
    let sanitizedFileName = this.sanitizeFileName(fileName);
    const csvData = this.convertArrayOfObjectsToCSV(data);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${sanitizedFileName}.csv`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private sanitizeFileName(filename: string) {
    // Define a regular expression to match invalid characters
    const invalidChars = /[\\\/:*?"<>|]/g;
    // Replace invalid characters with an underscore
    return filename.replace(invalidChars, '_');
  }


  logout() {
    this.routingService.navigate([RouteLinkGenerator.logout()]);
  }

  reload() {
    window.location.reload();
  }

  openAddContactDrawer() {
    this.onAddContactDrawerStateChanged.next(true);
  }

  closeAddContactDrawer() {
    this.onAddContactDrawerStateChanged.next(false);
  }

  openAddActivityModal(state: boolean = true, activityType: ActivityType | string, entityType: EntityType | null = null, entityId: string = '', isLog: boolean = false) {
    let options = new AddActivityModalOptions(state, activityType, entityType, entityId, isLog);
    this.onAddActivityModalStateChanged.next(options);
  }

  closeAddActivityModal(state: boolean = false) {
    let options = new AddActivityModalOptions(state);
    this.onAddActivityModalStateChanged.next(options);
  }

  showToast(title: string, options?: { type?: ToastType, timeout?: number; dismiss?: boolean; button?: { text: string; action: () => void; } }) {
    this.ymToastService.show(title, options);
  }

  copyToClipboard(content: string) {
    navigator.clipboard.writeText(content)
  }

  organisationChanged() {
    this.onOrganisationChanged.next('');
  }

  contactAdded(contact: ContactDocument) {
    this.onContactAdded.next(contact);
  }

  activityAdded(activity: ActivityDocument) {
    this.onActivityAdded.next(activity);
  }


  getBrowserInfo(): string {
    return navigator.userAgent || 'Unknown';
  }
}
